<template>
  <div
    v-if="!disabled"
    class="ai-block relative rounded-[18px] p-[1.5px] overflow-hidden"
    :class="{'is-calc': isLoading}">
    <!-- spinning conic border while calculating -->
    <div v-if="isLoading" class="ai-border-spin"/>

    <div class="relative bg-white rounded-[16.5px] px-4 pt-3.5 pb-4">
      <!-- header -->
      <div class="flex items-center gap-2.5 mb-1">
        <div
          class="w-[30px] h-[30px] rounded-[10px] shrink-0 flex items-center justify-center"
          style="background: linear-gradient(135deg,#8864ee,#6831d0)">
          <i class="pi pi-sparkles text-white text-sm" :class="{'ai-pulse': isLoading}"/>
        </div>
        <div class="flex-1 min-w-0">
          <div v-if="isLoading" class="text-[13px] font-bold ai-shimmer-text">מחשב תובנות על העסק…</div>
          <div v-else class="text-[13px] font-bold">ה-AI קרא את הנתונים שלך</div>
          <div class="text-[10px] text-gray-500">
            {{ isLoading ? 'עובר על הכנסות, תורים ולקוחות' : 'מבוסס על 30 הימים האחרונים' }}
          </div>
        </div>
      </div>

      <!-- skeleton insights + sweeping scan line -->
      <div v-if="isLoading" class="relative">
        <div
          v-for="(w, i) of [88, 72, 80]"
          :key="i"
          class="flex gap-2.5 py-2.5 border-b border-gray-100">
          <div class="w-[26px] h-[26px] rounded-lg shrink-0 ai-skel" :style="{animationDelay: `${i * 0.15}s`}"/>
          <div class="flex-1">
            <div class="h-[9px] rounded-full ai-skel" :style="{width: `${w}%`, animationDelay: `${i * 0.15}s`}"/>
            <div class="h-[9px] rounded-full mt-1.5 ai-skel" :style="{width: `${w - 30}%`, animationDelay: `${i * 0.15 + 0.1}s`}"/>
          </div>
        </div>
        <div class="ai-scanline"/>
      </div>

      <!-- error -->
      <div v-else-if="isError" class="flex items-center gap-2 py-3 text-xs text-gray-500">
        <i class="pi pi-exclamation-circle text-gray-400"/>
        לא הצלחנו לחשב תובנות כרגע.
        <span class="font-bold text-primary cursor-pointer" @click="fetchInsights">נסו שוב</span>
      </div>

      <!-- insights -->
      <template v-else>
        <div
          v-for="(ins, i) of insights"
          :key="i"
          class="flex gap-2.5 py-2.5 border-b border-gray-100 ai-reveal"
          :style="{animationDelay: `${i * 0.13}s`}">
          <div
            class="w-[26px] h-[26px] rounded-lg shrink-0 mt-0.5 flex items-center justify-center"
            :style="{background: iconStyle(ins.icon).tint}">
            <i :class="`pi ${iconStyle(ins.icon).pi}`" class="text-[12px]" :style="{color: iconStyle(ins.icon).fg}"/>
          </div>
          <div class="text-[12px] text-gray-700 leading-relaxed">
            {{ ins.text }}
            <span
              v-if="ins.cta"
              class="mr-1.5 font-bold text-primary cursor-pointer whitespace-nowrap"
              @click="onCta(ins.cta.action)">
              {{ ins.cta.label }} ←
            </span>
          </div>
        </div>
      </template>

      <!-- inline Q&A — tap a question, the answer expands right here -->
      <div
        class="transition-opacity duration-300"
        :class="isLoading ? 'opacity-30 pointer-events-none' : 'ai-reveal'"
        :style="isLoading ? {} : {animationDelay: '0.35s'}">
        <div v-if="questions.length" class="text-[11px] font-semibold text-gray-500 mt-3 mb-2">שאלות על העסק שלך</div>
        <div class="flex flex-col gap-1.5">
          <div
            v-for="(q, i) of questions"
            :key="q"
            class="rounded-[13px] border overflow-hidden transition-colors duration-150"
            :class="openQ === i ? 'border-primary-200 bg-primary-50' : 'border-gray-200 bg-white'">
            <div class="flex items-center gap-2 px-3 py-2.5 cursor-pointer" @click="toggleQuestion(i)">
              <span class="flex-1 text-xs font-semibold" :class="openQ === i ? 'text-primary-800' : 'text-gray-700'">{{ q }}</span>
              <i
                class="pi pi-chevron-down text-[11px] text-gray-400 shrink-0 transition-transform duration-200"
                :style="{transform: openQ === i ? 'rotate(180deg)' : 'none'}"/>
            </div>
            <div v-if="openQ === i" class="px-3 pb-2.5">
              <div v-if="answering" class="flex gap-1 py-1">
                <span v-for="k of 3" :key="k" class="w-[5.5px] h-[5.5px] rounded-full bg-primary-400 ai-dot" :style="{animationDelay: `${(k - 1) * 0.18}s`}"/>
              </div>
              <div v-else class="text-[12px] text-gray-700 leading-relaxed whitespace-pre-line">{{ answers[q] }}</div>
            </div>
          </div>
        </div>

        <!-- free-text — sending opens the chat tab -->
        <div class="flex items-center gap-2 border-[1.5px] border-gray-200 rounded-full py-1.5 ps-3.5 pe-2 bg-white mt-2.5">
          <i class="pi pi-sparkles text-primary-300 text-sm shrink-0"/>
          <input
            v-model="freeText"
            class="flex-1 min-w-0 border-none outline-none bg-transparent text-[12.5px] text-gray-900"
            placeholder="שאלה חופשית על העסק…"
            @keydown.enter="send"/>
          <button
            class="w-[30px] h-[30px] rounded-full shrink-0 flex items-center justify-center transition-colors duration-150"
            :style="freeText.trim()
              ? {background: 'linear-gradient(135deg,#8864ee,#6831d0)'}
              : {background: '#f4f4f5'}"
            @click="send">
            <i class="pi pi-arrow-left text-xs" :class="freeText.trim() ? 'text-white' : 'text-gray-400'"/>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {onMounted, ref} from 'vue';
import {
  AnalyticsAiCtaActionEnum,
  AnalyticsAiInsightIconEnum,
  IAnalyticsAiInsight,
} from '@torim-app/types';
import {AiApi} from '@app/api/admin/ai-api';
import {useNavigation} from '@app/composables/navigation';
import {AdminRouteNames} from '@app/router/route-names';
import {trackEvent} from '@app/composables/posthog';

const emit = defineEmits<{
  ask: [question: string];
  questions: [questions: string[]];
}>();

const {goTo} = useNavigation();

const isLoading = ref(true);
const isError = ref(false);
const disabled = ref(false);
const insights = ref<IAnalyticsAiInsight[]>([]);
const questions = ref<string[]>([]);
const openQ = ref<number | null>(null);
const answering = ref(false);
const answers = ref<Record<string, string>>({});
const freeText = ref('');

const iconStyle = (icon: AnalyticsAiInsightIconEnum): {pi: string; tint: string; fg: string} => {
  switch (icon) {
    case AnalyticsAiInsightIconEnum.Star:
      return {pi: 'pi-star-fill', tint: '#fef9c3', fg: '#a16207'};
    case AnalyticsAiInsightIconEnum.Cancel:
      return {pi: 'pi-times-circle', tint: '#fee2e2', fg: '#b91c1c'};
    case AnalyticsAiInsightIconEnum.Clock:
      return {pi: 'pi-clock', tint: '#edeafd', fg: '#6831d0'};
    case AnalyticsAiInsightIconEnum.Money:
      return {pi: 'pi-money-bill', tint: '#dcfce7', fg: '#15803d'};
    case AnalyticsAiInsightIconEnum.Users:
      return {pi: 'pi-users', tint: '#dbeafe', fg: '#1d4ed8'};
    default:
      return {pi: 'pi-chart-line', tint: '#edeafd', fg: '#6831d0'};
  }
};

const ctaRoutes: Record<AnalyticsAiCtaActionEnum, AdminRouteNames> = {
  [AnalyticsAiCtaActionEnum.Automations]: AdminRouteNames.Communication,
  [AnalyticsAiCtaActionEnum.LoyaltyProgram]: AdminRouteNames.LoyaltyProgram,
  [AnalyticsAiCtaActionEnum.Services]: AdminRouteNames.Configurations,
  [AnalyticsAiCtaActionEnum.Payments]: AdminRouteNames.Payments,
};

const onCta = (action: AnalyticsAiCtaActionEnum) => {
  trackEvent('analytics_ai_insight_cta_clicked', {action});
  goTo(ctaRoutes[action] ?? AdminRouteNames.Home);
};

// Insights cover a fixed 30-day window and are cached server-side per day,
// so this fetches once on mount — changing the page's date filter doesn't refetch.
const fetchInsights = async () => {
  isLoading.value = true;
  isError.value = false;
  openQ.value = null;
  answers.value = {};
  try {
    const res = await AiApi.getAnalyticsInsights();
    if (!res.enabled) {
      disabled.value = true;
      return;
    }
    insights.value = res.insights;
    questions.value = res.suggestedQuestions;
    emit('questions', res.suggestedQuestions);
  } catch (e) {
    console.error(e);
    isError.value = true;
  } finally {
    isLoading.value = false;
  }
};

const toggleQuestion = async (i: number) => {
  if (openQ.value === i) {
    openQ.value = null;
    return;
  }
  openQ.value = i;
  const q = questions.value[i];
  if (answers.value[q] || answering.value) return;
  trackEvent('analytics_ai_question_opened', {question: q});
  answering.value = true;
  try {
    const res = await AiApi.sendAnalyticsChatMessage({
      message: q,
      conversationHistory: [],
    });
    answers.value[q] = res.reply;
  } catch (e) {
    console.error(e);
    answers.value[q] = 'לא הצלחתי לענות על זה כרגע. נסו שוב מאוחר יותר.';
  } finally {
    answering.value = false;
  }
};

const send = () => {
  const q = freeText.value.trim();
  if (!q) return;
  freeText.value = '';
  emit('ask', q);
};

onMounted(fetchInsights);
</script>

<style scoped lang="scss">
.ai-block {
  background: linear-gradient(135deg, #c3b9f9, #8864ee 45%, #e54ca1);
}

.ai-border-spin {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 250%;
  padding-top: 250%;
  margin: -125% 0 0 -125%;
  background: conic-gradient(#8864ee, #e54ca1, #ffaa1a, #8864ee);
  animation: ai-border-spin 1.4s linear infinite;
}

@keyframes ai-border-spin {
  from { transform: rotate(0); }
  to { transform: rotate(360deg); }
}

.ai-pulse {
  display: inline-flex;
  animation: ai-pulse 1.1s ease-in-out infinite;
}

@keyframes ai-pulse {
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(0.75); opacity: 0.6; }
}

.ai-shimmer-text {
  background: linear-gradient(90deg, #6831d0, #e54ca1, #8864ee, #6831d0);
  background-size: 200% auto;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  animation: ai-shimmer 1.5s linear infinite;
}

@keyframes ai-shimmer {
  from { background-position: 200% center; }
  to { background-position: -200% center; }
}

.ai-skel {
  background: linear-gradient(90deg, #f4f4f5 25%, #ddd8fc 50%, #f4f4f5 75%);
  background-size: 200% auto;
  animation: ai-shimmer 1.3s linear infinite;
}

.ai-scanline {
  position: absolute;
  left: -6px;
  right: -6px;
  height: 2px;
  border-radius: 999px;
  background: linear-gradient(90deg, transparent, #8864ee 30%, #e54ca1 70%, transparent);
  box-shadow: 0 0 14px 3px rgba(136, 100, 238, 0.35);
  animation: ai-scan 1.7s ease-in-out infinite;
}

@keyframes ai-scan {
  0% { top: 0; opacity: 0; }
  12% { opacity: 1; }
  88% { opacity: 1; }
  100% { top: calc(100% - 2px); opacity: 0; }
}

.ai-reveal {
  animation: ai-reveal-in 0.4s cubic-bezier(0.22, 1, 0.36, 1) both;
}

@keyframes ai-reveal-in {
  from { opacity: 0; transform: translateY(7px); }
  to { opacity: 1; transform: translateY(0); }
}

.ai-dot {
  animation: ai-dot 1.1s infinite;
}

@keyframes ai-dot {
  0%, 80%, 100% { opacity: 0.25; }
  40% { opacity: 1; }
}
</style>
